/**
 * Task Attachments Component
 * Displays, uploads, and manages file attachments for a task
 */

import { useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { formatBytes } from '@/lib/utils';
import { Paperclip, Upload, Download, Trash2, FileText, Image, File, Loader2 } from 'lucide-react';
import { useTaskAttachments, useUploadTaskAttachment, useDeleteTaskAttachment } from '../hooks/useTasks';
import { getAttachmentDownloadUrl, type Attachment } from '../api';
import { formatDistanceToNow } from 'date-fns';

interface TaskAttachmentsProps {
  taskId: string;
  canEditTasks?: boolean;
}


function getFileIcon(mimeType?: string) {
  if (mimeType?.startsWith('image/')) {
    return <Image className="h-4 w-4 text-blue-500" />;
  }
  if (mimeType?.includes('pdf')) {
    return <FileText className="h-4 w-4 text-red-500" />;
  }
  return <File className="h-4 w-4 text-gray-500" />;
}

function AttachmentItem({
  attachment,
  taskId,
  onDelete,
  isDeleting,
  canEditTasks,
}: {
  attachment: Attachment;
  taskId: string;
  onDelete: (attachmentId: string) => void;
  isDeleting: boolean;
  canEditTasks: boolean;
}) {
  const downloadUrl = getAttachmentDownloadUrl(taskId, attachment.id);

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {getFileIcon(attachment.mime_type)}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{attachment.original_filename}</p>
          <p className="text-xs text-muted-foreground">
            {attachment.size_bytes ? formatBytes(attachment.size_bytes) : ''}
            {attachment.uploaded_by_name && ` • ${attachment.uploaded_by_name}`}
            {attachment.created_at &&
              ` • ${formatDistanceToNow(new Date(attachment.created_at), { addSuffix: true })}`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 ml-2">
        <Button
          variant="ghost"
          size="icon"
          asChild
          className="h-8 w-8"
          aria-label={`Download ${attachment.original_filename}`}
        >
          <a href={downloadUrl} download={attachment.original_filename} target="_blank" rel="noopener noreferrer">
            <Download className="h-4 w-4" aria-hidden="true" />
          </a>
        </Button>
        {canEditTasks && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => onDelete(attachment.id)}
            disabled={isDeleting}
            aria-label={`Delete ${attachment.original_filename}`}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

export function TaskAttachments({ taskId, canEditTasks = false }: TaskAttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: attachments = [], isLoading } = useTaskAttachments(taskId);
  const uploadAttachment = useUploadTaskAttachment();
  const deleteAttachment = useDeleteTaskAttachment();

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Clear the input immediately to prevent duplicate uploads on re-render
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Track successful and failed uploads
      let successCount = 0;
      const errors: string[] = [];

      for (const file of Array.from(files)) {
        try {
          await uploadAttachment.mutateAsync({ taskId, file });
          successCount++;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Upload failed';
          errors.push(`${file.name}: ${message}`);
        }
      }

      // Show appropriate toast
      if (successCount > 0 && errors.length === 0) {
        toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully`);
      } else if (errors.length > 0) {
        if (successCount > 0) {
          toast.warning(`${successCount} uploaded, ${errors.length} failed`);
        }
        errors.forEach((err) => toast.error(err));
      }
    },
    [taskId, uploadAttachment]
  );

  const handleDelete = useCallback(
    async (attachmentId: string) => {
      try {
        await deleteAttachment.mutateAsync({ taskId, attachmentId });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete attachment';
        toast.error(message);
      }
    },
    [taskId, deleteAttachment]
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Upload Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Paperclip className="h-4 w-4" />
          <span>{attachments.length} attachment{attachments.length !== 1 ? 's' : ''}</span>
        </div>
        {canEditTasks && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUploadClick}
              disabled={uploadAttachment.isPending}
            >
              {uploadAttachment.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload File
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              accept="*/*"
            />
          </>
        )}
      </div>

      {/* Attachments List */}
      {attachments.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No attachments yet</p>
          {canEditTasks && <p className="text-xs">Upload files to attach them to this task</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <AttachmentItem
              key={attachment.id}
              attachment={attachment}
              taskId={taskId}
              onDelete={handleDelete}
              canEditTasks={canEditTasks}
              isDeleting={
                deleteAttachment.isPending &&
                deleteAttachment.variables?.attachmentId === attachment.id
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
